import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IoService } from '../../../services/io/io.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { AppComponent } from 'src/app/app.component';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';

// One table row: an existing Vendor Product linked to this retail product, or
// a newly added one. Prices are edited in dollars and converted to integer
// cents on save. Case Qty edits only touch this retail item's option entry —
// a Vendor Product linked to other retail items keeps those links untouched.
interface DistributorRow {
  vendorProductId: string;
  source: any;
  distributorId: string;
  distributorName: string;
  price: number;
  caseQty: number;
  barcode: string;
  // Some tenants store vendor product barcodes as arrays (import convention);
  // preserve whichever shape the document already had on save.
  barcodeWasArray: boolean;
  otherItemCount: number;
  original: { distributorId: string, priceCents: number, caseQty: number, barcode: string };
  isNew: boolean;
  removed: boolean;
  editingDistributor: boolean;
  searchText: string;
  results: any[];
}

@Component({
  selector: 'app-distributor-management',
  templateUrl: './distributor-management.component.html',
  styleUrls: ['./distributor-management.component.scss']
})
export class DistributorManagementComponent implements OnInit, OnDestroy {

  viewKey: string;
  documentId: string;

  accountName: string = null;
  // The retail item's rolling average cost in cents (when the tenant stores one),
  // used to sanity-check a saved price against what the item actually costs.
  accountAverageCost: number = null;
  rows: DistributorRow[] = [];
  loading = false;
  saving = false;
  loadError: string = null;
  errors: string[] = [];

  // Full vendor list (type 'vendor' Locations), filtered client side per row.
  vendors: any[] = [];

  // Whether distributor names link to the vendor-product View (permission-gated).
  canOpenVendorProduct = false;

  private paramsSub: Subscription;
  private permissionsSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ioService: IoService,
    private authorizationService: AuthorizationService,
  ) { }

  ngOnInit() {
    // Permissions load asynchronously on a fresh page load — evaluate now and
    // again once they arrive.
    this.checkVendorProductPermission();
    this.permissionsSub = this.authorizationService.permissionsLoadedObservable().subscribe(() => {
      this.checkVendorProductPermission();
    });
    this.paramsSub = this.route.paramMap.subscribe(params => {
      this.viewKey = params.get('viewKey');
      this.documentId = params.get('documentId');
      this.load();
    });
  }

  ngOnDestroy() {
    if (this.paramsSub) this.paramsSub.unsubscribe();
    if (this.permissionsSub) this.permissionsSub.unsubscribe();
  }

  private checkVendorProductPermission() {
    this.canOpenVendorProduct = this.authorizationService.checkPermission('view.vendor-product.view', 'any', false);
  }

  async load() {
    this.loading = true;
    this.loadError = null;
    this.errors = [];
    try {
      const account: any = await this.ioService.post('/inventory/getInventoryAccount', {
        accountId: this.documentId,
      });
      this.accountName = account.name;
      this.accountAverageCost = account.rollingAverageCost > 0 ? account.rollingAverageCost : null;

      // getInventoryAccount only returns vendorProducts for Vendor.manage holders.
      if (!account.vendorProducts) {
        this.loadError = 'Vendor management permission is required to manage vendors.';
        this.loading = false;
        return;
      }

      const vendors: any = await this.ioService.post('/location/getLocations', {
        type: 'vendor',
      });
      this.vendors = vendors || [];
      const vendorNames: { [id: string]: string } = {};
      for (const vendor of this.vendors) {
        vendorNames[String(vendor._id)] = vendor.name;
      }

      this.rows = account.vendorProducts.map(vendorProduct => this.toRow(vendorProduct, vendorNames));
      this.rows.sort((a, b) => (a.distributorName || '').localeCompare(b.distributorName || ''));
    } catch (e) {
      this.loadError = e?.error?.msg || 'Failed to load vendors.';
    }
    this.loading = false;
  }

  private toRow(vendorProduct: any, vendorNames: { [id: string]: string }): DistributorRow {
    const distributorId = vendorProduct.locationId && vendorProduct.locationId.length > 0
      ? String(vendorProduct.locationId[0]) : null;

    let caseQty = null;
    let otherItemCount = 0;
    for (const group of vendorProduct.options || []) {
      for (const subOption of group.options || []) {
        if (String(subOption.accountId) === this.documentId) {
          caseQty = subOption.qty;
        } else {
          otherItemCount++;
        }
      }
    }

    const priceCents = vendorProduct.price !== null && vendorProduct.price !== undefined ? vendorProduct.price : null;
    const barcodeWasArray = Array.isArray(vendorProduct.barcode);
    const barcode = barcodeWasArray ? vendorProduct.barcode.join(', ') : (vendorProduct.barcode || '');

    return {
      vendorProductId: String(vendorProduct._id),
      source: vendorProduct,
      distributorId,
      distributorName: distributorId ? (vendorNames[distributorId] || vendorProduct.locationName || '') : '',
      price: priceCents !== null ? priceCents / 100 : null,
      caseQty,
      barcode,
      barcodeWasArray,
      otherItemCount,
      original: {
        distributorId,
        priceCents,
        caseQty,
        barcode,
      },
      isNew: false,
      removed: false,
      editingDistributor: false,
      searchText: '',
      results: [],
    };
  }

  // ----- Distributor search / row management -----

  addRow() {
    this.rows.push({
      vendorProductId: null,
      source: null,
      distributorId: null,
      distributorName: '',
      price: null,
      caseQty: null,
      barcode: '',
      barcodeWasArray: true,
      otherItemCount: 0,
      original: null,
      isNew: true,
      removed: false,
      editingDistributor: true,
      searchText: '',
      results: [],
    });
  }

  // Only rows that don't exist yet pick their distributor inline; existing
  // rows change distributor through the vendor product's own View.
  editDistributor(row: DistributorRow) {
    if (!row.isNew || row.removed || this.saving) return;
    row.editingDistributor = true;
    row.searchText = '';
    row.results = [];
  }

  // Clicking an existing row's distributor opens that Vendor Product's full
  // View (browser Back returns here). Staged edits get a discard prompt first.
  async openVendorProduct(row: DistributorRow) {
    if (row.isNew || row.removed || !row.vendorProductId || this.saving || !this.canOpenVendorProduct) return;
    if (!await this.confirmDiscardChanges()) return;
    this.router.navigate(['details', 'vendor-product', row.vendorProductId], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'preserve',
    });
  }

  private async confirmDiscardChanges(): Promise<boolean> {
    if (!this.hasChanges()) return true;
    const confirmRef = AppComponent.showMessage(
      'Unsaved Changes',
      'Discard your vendor changes?',
      ['Discard', 'Cancel'],
      false,
    );
    const confirmResult: DialogOption = await confirmRef.afterClosed().toPromise();
    return !!confirmResult && confirmResult.value === 'Discard';
  }

  searchDistributors(row: DistributorRow) {
    const text = row.searchText.trim().toLowerCase();
    if (!text) {
      row.results = [];
      return;
    }
    row.results = this.vendors
      .filter(vendor => (vendor.name || '').toLowerCase().includes(text))
      .slice(0, 8);
  }

  selectDistributor(row: DistributorRow, vendor: any) {
    row.distributorId = String(vendor._id);
    row.distributorName = vendor.name;
    row.editingDistributor = false;
    row.searchText = '';
    row.results = [];
  }

  cancelDistributorEdit(row: DistributorRow) {
    if (row.isNew && !row.distributorId) {
      this.rows = this.rows.filter(r => r !== row);
      return;
    }
    row.editingDistributor = false;
    row.searchText = '';
    row.results = [];
  }

  removeRow(row: DistributorRow) {
    if (row.isNew) {
      this.rows = this.rows.filter(r => r !== row);
    } else {
      row.removed = !row.removed;
    }
  }

  // ----- Save -----

  private toCents(dollars: number): number {
    return Math.round(dollars * 100);
  }

  // Serialize the edited barcode text back to the document's original shape:
  // arrays stay arrays (comma-separated input supports multiple), scalars stay
  // scalar strings.
  private serializeBarcode(row: DistributorRow): any {
    const values = (row.barcode || '').split(',').map(value => value.trim()).filter(value => value !== '');
    if (row.barcodeWasArray) {
      return values.length > 0 ? values : null;
    }
    return values.length > 0 ? values.join(', ') : null;
  }

  rowInvalid(row: DistributorRow): boolean {
    if (row.removed) return false;
    return !row.distributorId
      || row.price === null || row.price === undefined || String(row.price) === ''
      || row.caseQty === null || row.caseQty === undefined || String(row.caseQty) === '' || row.caseQty <= 0;
  }

  private rowChanged(row: DistributorRow): boolean {
    if (row.isNew || row.removed) return true;
    return row.distributorId !== row.original.distributorId
      || (row.price !== null && String(row.price) !== '' ? this.toCents(row.price) : null) !== row.original.priceCents
      || Number(row.caseQty) !== row.original.caseQty
      || (row.barcode || '') !== row.original.barcode;
  }

  hasChanges(): boolean {
    return this.rows.some(row => this.rowChanged(row));
  }

  // Full pass-through update: /inventory/updateVendorProduct $sets every field
  // it's given (and drops options it isn't), so untouched values are carried
  // over from the loaded document and only this row's edits are applied.
  private buildUpdatePayload(source: any, row: DistributorRow): any {
    const options = JSON.parse(JSON.stringify(source.options || []));
    let matched = false;
    for (const group of options) {
      for (const subOption of group.options || []) {
        if (String(subOption.accountId) === this.documentId) {
          subOption.qty = Number(row.caseQty);
          matched = true;
        }
      }
    }
    if (!matched) {
      options.push({
        key: null,
        type: 'components',
        options: [{
          key: null,
          accountId: this.documentId,
          name: this.accountName,
          qty: Number(row.caseQty),
        }],
      });
    }

    return {
      _id: source._id,
      name: source.name,
      description: source.description,
      price: this.toCents(row.price),
      unitOfMeasure: source.unitOfMeasure,
      unitDescription: source.unitDescription,
      barcode: this.serializeBarcode(row),
      sku: source.sku,
      remoteId: source.remoteId,
      // Keep any extra vendor locations unless the distributor itself changed.
      locationId: (row.original && row.distributorId === row.original.distributorId)
        ? source.locationId
        : [row.distributorId],
      categoryId: source.categoryId,
      vendorBuyGroupId: source.vendorBuyGroupId,
      options,
    };
  }

  // Guard rail: a price that jumps by more than 3x in either direction, or a
  // per-unit price (price / case qty) more than 3x off the item's average cost,
  // is almost always a basis mistake (carton price on a pack, case price with
  // Case Qty 1, per-can price on a multipack). Belmont 2026-09-29: 49 such
  // saves in one day. List them and make the user confirm before saving.
  static readonly PRICE_GUARD_RATIO = 3;
  private static money(cents: number): string {
    return '$' + (cents / 100).toFixed(2);
  }
  priceWarnings(): string[] {
    const warnings: string[] = [];
    const ratio = DistributorManagementComponent.PRICE_GUARD_RATIO;
    for (const row of this.rows) {
      if (row.removed || this.rowInvalid(row) || !this.rowChanged(row)) continue;
      const cents = this.toCents(row.price);
      const name = row.distributorName || 'New vendor';
      const previous = row.isNew ? null : row.original.priceCents;
      if (previous > 0 && cents !== previous && (cents > previous * ratio || cents < previous / ratio)) {
        const factor = cents > previous ? (cents / previous) : (previous / cents);
        warnings.push(`${name}: ${DistributorManagementComponent.money(previous)} → ${DistributorManagementComponent.money(cents)}`
          + ` (${factor.toFixed(factor >= 10 ? 0 : 1)}× ${cents > previous ? 'higher' : 'lower'} than the current price)`);
        continue;
      }
      const caseQty = Number(row.caseQty) > 0 ? Number(row.caseQty) : 1;
      const unit = cents / caseQty;
      const average = this.accountAverageCost;
      if (average > 0 && (unit > average * ratio || unit < average / ratio)) {
        warnings.push(`${name}: ${DistributorManagementComponent.money(cents)} for a case of ${caseQty} = `
          + `${DistributorManagementComponent.money(unit)} per unit, but this item's average cost is ${DistributorManagementComponent.money(average)}`
          + (caseQty === 1 ? ' — is Case Qty right?' : ''));
      }
    }
    return warnings;
  }

  async save() {
    if (this.saving) return;
    this.errors = [];

    const invalidRows = this.rows.filter(row => this.rowInvalid(row));
    if (invalidRows.length > 0) {
      this.errors = ['Vendor, price, and case qty are required on every row.'];
      return;
    }
    const priceWarnings = this.priceWarnings();
    if (priceWarnings.length > 0) {
      const guardRef = AppComponent.showMessage(
        'Check These Prices',
        'The price is entered per case (Case Qty units). These look like a different basis:\n\n'
          + priceWarnings.map(line => '• ' + line).join('\n')
          + '\n\nSave them anyway?',
        ['Save Anyway', 'Cancel'],
        false,
        '560px',
        'auto',
      );
      const guardResult: DialogOption = await guardRef.afterClosed().toPromise();
      if (!guardResult || guardResult.value !== 'Save Anyway') {
        return;
      }
    }

    // Removing a Vendor Product removes it for every retail item it's linked
    // to, not just this one — make sure that's understood before deleting.
    const multiRemoves = this.rows.filter(row => row.removed && !row.isNew && row.otherItemCount > 0);
    if (multiRemoves.length > 0) {
      const names = multiRemoves.map(row => row.distributorName).join(', ');
      const confirmRef = AppComponent.showMessage(
        'Remove Vendors',
        `Removing ${names} also removes those vendor products for the other items they supply. Continue?`,
        ['Remove', 'Cancel'],
        false,
      );
      const confirmResult: DialogOption = await confirmRef.afterClosed().toPromise();
      if (!confirmResult || confirmResult.value !== 'Remove') {
        return;
      }
    }

    this.saving = true;
    try {
      for (const row of this.rows) {
        if (row.removed && !row.isNew) {
          try {
            await this.ioService.post('/inventory/deleteVendorProduct', {
              accountId: row.vendorProductId,
            });
          } catch (e) {
            // The server refuses while the product sits on open transfers and
            // names them. Show them and let the user delete anyway (the
            // transfers then show the line as deleted with Remove / Replace).
            const openTransfers = e && e.error && Array.isArray(e.error.openTransfers) ? e.error.openTransfers : null;
            if (!openTransfers) {
              throw e;
            }
            const list = openTransfers.map(t => t.key + ' — ' + (t.typeName || t.type) + ', ' + (t.statusName || t.status)
              + (t.customerName ? ', ' + t.customerName : '')).join('\n');
            const ref = AppComponent.showMessage(
              'On Open Transfers',
              `${row.distributorName || 'This vendor product'} is still on ${openTransfers.length} open transfer${openTransfers.length === 1 ? '' : 's'}:\n\n${list}\n\n`
                + 'Those transfers cannot complete until the line is removed or replaced. Delete anyway?',
              ['Delete Anyway', 'Keep'],
              false,
              '620px',
              'auto',
            );
            const result: DialogOption = await ref.afterClosed().toPromise();
            if (!result || result.value !== 'Delete Anyway') {
              row.removed = false;
              continue;
            }
            await this.ioService.post('/inventory/deleteVendorProduct', {
              accountId: row.vendorProductId,
              force: true,
            });
          }
          continue;
        }
        if (row.isNew) {
          const created: any = await this.ioService.post('/inventory/addVendorProduct', {
            name: this.accountName,
            cost: this.toCents(row.price),
            barcode: this.serializeBarcode(row),
            retailProduct: {
              _id: this.documentId,
              name: this.accountName,
            },
          });
          await this.ioService.post('/inventory/updateVendorProduct', {
            account: this.buildUpdatePayload(created, row),
          });
          continue;
        }
        if (this.rowChanged(row)) {
          await this.ioService.post('/inventory/updateVendorProduct', {
            account: this.buildUpdatePayload(row.source, row),
          });
        }
      }
      AppComponent.showSnack('Vendors saved.', 'OK', 3000);
      await this.load();
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to save vendors.'];
    }
    this.saving = false;
  }

  async close() {
    if (!await this.confirmDiscardChanges()) {
      return;
    }
    this.router.navigate(['details', this.viewKey, this.documentId], {
      relativeTo: this.route.parent,
      queryParamsHandling: 'preserve',
    });
  }
}
